
import { Component, Provide, Prop, Mixins } from "vue-property-decorator";
import { mapGetters } from "vuex";
import _ from "@/boot/lodash";

// Mixins
import RequestPromises from "@/mixins/requestPromises";
import Context from "@/mixins/context";

// Enums/Data
import { DataModules } from "@/store/modules/data";
import { OrgConfigKeys } from "@/data/constants";

// Types
import {
  IProvisionProviderData,
  IProvisionProviderMethods
} from "@/models/providers/provisioning";
import { IBasketProvisioningFieldsForm } from "@/models/provisioning";
import { IClient } from "@/models/clients";
import { IBasket } from "@/models/baskets";
import { QUERY_PARAMS } from "@/data/constants";
import { IProduct } from "@/models/products";
import { IProvider } from "@/models/providers";
import { CreateElement } from "vue";

@Component({
  computed: {
    ...mapGetters({
      scope: "data/provisioning/basketProducts/scope",
      productScope: "data/provisioning/basketProducts/productScope",
      valuesScope: "data/provisioning/basketProducts/valuesScope"
    })
  }
})
export default class ProvisioningProvider
  extends Mixins(RequestPromises, Context)
  implements IProvider {
  // Props
  @Prop({ default: false })
  public handleLoading!: boolean;

  @Prop({ default: () => ({}) })
  public loaderAttrs!: Record<string, any>;

  @Prop({ default: true })
  public handleRequestError!: boolean;

  // Getters
  public scope!: Function;
  public productScope!: Function;
  public valuesScope!: Function;

  // Provide
  @Provide("provisionProviderData")
  public providerData(): IProvisionProviderData {
    return {
      hasProvisioning: this.hasProvisioning,
      hasRequestError: this.hasRequestError,
      isLoading: this.isLoading,
      isProcessing: this.isProcessing,
      isReloading: this.isReloading,
      pathBasketProductId: this.pathBasketProductId,
      provisionErrors: this.provisionErrors
    };
  }

  @Provide("provisionProviderMethods")
  public get providerMethods(): IProvisionProviderMethods {
    return {
      getOrgProvisioning: this.getOrgProvisioning,
      getBasketProductFields: this.getBasketProductFields,
      getProductFields: this.getProductFields,
      destroyBasketProductFields: this.destroyBasketProductFields,
      destroyProductFields: this.destroyProductFields,
      getFieldsValues: this.getFieldsValues,
      destroyFieldsValues: this.destroyFieldsValues,
      basketProductFields: this.basketProductFields,
      productFields: this.productFields,
      basketProductFieldsValues: this.basketProductFieldsValues,
      updateBasketProductFields: this.updateBasketProductFields,
      checkConfiguration: this.checkConfiguration,
      isProvisionable: this.isProvisionable,
      reloadData: this.reloadData
    };
  }

  // Data
  public isLoading = false;
  public isReloading = false;
  public hasRequestError = false;
  public isProcessing = false;

  public provisionErrors = {};

  // Initial Data
  public async created() {
    this.isLoading = true;
    await this.loadData();
  }

  public async loadData() {
    try {
      await this.getOrgProvisioning(this.isReloading);
      this.hasRequestError = false;
    } catch (error) {
      this.hasRequestError = true;
      this.$store.dispatch("api/handleError", error);
    } finally {
      this.isLoading = false;
      this.isReloading = false;
    }
  }

  public reloadData() {
    this.isReloading = true;
    return this.loadData();
  }

  public getOrgProvisioning(ignoreStored = false) {
    return this.$store.dispatch("org/getConfig", {
      ignoreStored,
      keys: [OrgConfigKeys.PRODUCT_PROVISIONING_ENABLED]
    });
  }

  public get hasProvisioning() {
    return this.$store.getters["org/hasProvisioningEnabled"];
  }

  public get pathBasketProductId() {
    return _.get(this.$route, `query.${QUERY_PARAMS.BASKET_PRODUCT_ID}`, "");
  }

  public getBasketProductFields(
    basketId: IBasket["id"],
    productId: IProduct["id"],
    subProductIds: IProduct["id"][] = [],
    ignoreStored = false
  ) {
    return this.$store.dispatch(
      `data/${DataModules.PROVISIONING_BASKET_PRODUCTS}/listFields`,
      {
        basketId,
        productId,
        subProductIds,
        scope: this.scope(basketId, productId),
        vm: this,
        ignoreStored
      }
    );
  }

  public getProductFields(
    productId: IProduct["id"],
    clientId: IClient["id"],
    subProductIds: IProduct["id"][] = []
  ) {
    return this.$store.dispatch(
      `data/${DataModules.PROVISIONING_BASKET_PRODUCTS}/listProductFields`,
      _.merge(
        {
          productId,
          subProductIds,
          scope: this.productScope(productId),
          vm: this
        },
        clientId ? { clientId } : {}
      )
    );
  }

  public destroyBasketProductFields(
    basketId: IBasket["id"],
    productId: IProduct["id"]
  ) {
    this.$store.dispatch("data/binList", {
      storeModule: DataModules.PROVISIONING_BASKET_PRODUCTS,
      scope: this.scope(basketId, productId),
      vm: this
    });
  }

  public destroyProductFields(productId: IProduct["id"]) {
    this.$store.dispatch("data/binList", {
      storeModule: DataModules.PROVISIONING_BASKET_PRODUCTS,
      scope: this.productScope(productId),
      vm: this
    });
  }

  public getFieldsValues(
    basketId: IBasket["id"],
    productId: IProduct["id"],
    subProductIds: IProduct["id"][] = [],
    ignoreStored = false
  ) {
    return this.$store.dispatch(
      `data/${DataModules.PROVISIONING_BASKET_PRODUCTS}/getFieldsValues`,
      {
        basketId,
        productId,
        subProductIds,
        scope: this.valuesScope(basketId, productId),
        vm: this,
        ignoreStored
      }
    );
  }

  public destroyFieldsValues(
    basketId: IBasket["id"],
    productId: IProduct["id"]
  ) {
    this.$store.dispatch("data/binList", {
      storeModule: DataModules.PROVISIONING_BASKET_PRODUCTS,
      scope: this.valuesScope(basketId, productId),
      vm: this
    });
  }

  public basketProductFields(
    basketId: IBasket["id"],
    productId: IProduct["id"]
  ) {
    return _.get(
      this.$store,
      `state.data.provisioning.basketProducts.${this.scope(
        basketId,
        productId
      )}.data`
    );
  }

  public productFields(productId: IProduct["id"]) {
    return _.get(
      this.$store,
      `state.data.provisioning.basketProducts.${this.productScope(
        productId
      )}.data`
    );
  }

  public basketProductFieldsValues(
    basketId: IBasket["id"],
    productId: IProduct["id"]
  ) {
    return _.get(
      this.$store,
      `state.data.provisioning.basketProducts.${this.valuesScope(
        basketId,
        productId
      )}.data`
    );
  }

  public async checkConfiguration(basketId: IBasket["id"]) {
    try {
      await this.checkProvisionConfiguration(basketId);
      this.provisionErrors = {};
      return Promise.resolve();
    } catch (error) {
      // Type 666 means special privisioning error which we need to map as 422, special type added by Harry :)
      if (
        !this.$store.getters["api/is"](error, 409) ||
        _.get(error, "response.data.error.type") !== 666
      ) {
        throw error;
      }

      this.provisionErrors = _.get(error, "response.data.error");
      return Promise.resolve();
    }
  }

  public async isProvisionable(
    productId: IProduct["id"],
    clientId: IClient["id"]
  ) {
    let provisionFields = {};

    if (this.hasProvisioning) {
      await this.getProductFields(productId, clientId);
      provisionFields = this.productFields(productId);
      this.destroyProductFields(productId);
    }
    return !_.isEmpty(provisionFields);
  }

  public checkProvisionConfiguration(basketId: IBasket["id"]) {
    return this.$store.dispatch(
      `data/${DataModules.PROVISIONING_BASKET_PRODUCTS}/checkConfiguration`,
      {
        basketId,
        vm: this,
        ignoreStored: true
      }
    );
  }

  public updateBasketProductFields(
    basketId: IBasket["id"],
    productId: IProduct["id"],
    data: IBasketProvisioningFieldsForm
  ) {
    return this.$store.dispatch(
      `data/${DataModules.PROVISIONING_BASKET_PRODUCTS}/updateValues`,
      {
        basketId,
        productId,
        data
      }
    );
  }

  public clearProvisionErrors() {
    this.provisionErrors = {};
  }

  // Life-cycle Methods
  public render(h: CreateElement) {
    // Handle loading
    if (this.handleLoading && this.isLoading)
      return h("is-loading", { props: { if: true }, attrs: this.loaderAttrs });
    // Handle request error
    if (this.handleRequestError && this.hasRequestError)
      return h("request-error", {
        props: { isLoading: this.isLoading && this.hasRequestError },
        on: { click: () => this.reloadData() }
      });
    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $provisionData: this.providerData(),
        $provisionMethods: this.providerMethods
      })
    );
  }
}
